import moment from 'moment';
import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useCreateCompanyPreferenceMutation, useListCompanyPreferenceQuery } from '../../../../redux/apis/companyPreference.api';

export const useBizview = (defaultOffsetMonths) => {
  // State management
  const [fy, setFy] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [subAnchorEl, setSubAnchorEl] = useState(null);
  const [selectedType, setSelectedType] = useState('');
  const [selectedVal, setSelectedVal] = useState('');
  const [customOpen, setCustomOpen] = useState(false);
  const [customDates, setCustomDates] = useState({
    start: null,
    end: null,
  });

  // Common loading state for both periods
  const [isCommonLoading, setIsCommonLoading] = useState(false);

  // Refs to prevent unnecessary re-renders
  const isInitialized = useRef(false);
  const lastProcessedPreference = useRef(null);
  const loadingTimeoutRef = useRef(null);

  // API hooks with proper error handling
  const [createCompanyPreference, { isLoading: creating, error: createError }] = useCreateCompanyPreferenceMutation();
  const {
    data: companyPreference,
    isLoading,
    isFetching,
    error: fetchError,
    isSuccess: isPreferenceLoaded,
  } = useListCompanyPreferenceQuery();

  // Redux selectors
  const userPreference = useSelector((state) => state?.userPrefrenceandReducer);
  const { financialYear, listFYData } = userPreference || {};

  // Common loading state management functions
  const startCommonLoading = useCallback(() => {
    setIsCommonLoading(true);

    // Clear any existing timeout
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
    }

    // Auto-stop loading after 10 seconds as fallback
    loadingTimeoutRef.current = setTimeout(() => {
      setIsCommonLoading(false);
    }, 10000);
  }, []);

  const stopCommonLoading = useCallback(() => {
    setIsCommonLoading(false);
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }
  }, []);

  const saveCompanyPreference = useCallback(
    async ({ attributetypecd, additionalvalue, attributevalue, selectedType }) => {
      try {
        await createCompanyPreference({
          createCompanyPreferenceInput: {
            attributetypecd,
            additionalvalue: `${additionalvalue}/${selectedType}`,
            attributevalue,
          },
        }).unwrap();
      } catch (error) {
        console.error('Failed to save company preference:', error);
        // Handle error appropriately - could show toast notification
      }
    },
    [createCompanyPreference]
  );

  const financialYears = useMemo(() => {
    if (!listFYData?.length) return [];

    return listFYData
      .map((item) => ({
        label: item?.startyear && item?.endyear ? `FY ${item.startyear}-${item.endyear}` : null,
        value: item?.financialyearid || '',
        startTimestamp: item?.startdate || null,
        endTimestamp: item?.enddate || null,
        companyid: item?.companyid || '',
      }))
      .filter((item) => item.label) // Filter out null labels
      .sort((a, b) => {
        const yearA = parseInt(a.label?.split(' ')[1]?.split('-')[0]) || 0;
        const yearB = parseInt(b.label?.split(' ')[1]?.split('-')[0]) || 0;
        return yearA - yearB;
      });
  }, [listFYData]);

  const getDynamicMonths = useCallback((startTs, endTs) => {
    const start = moment.unix(startTs);
    const end = moment.unix(endTs);
    const months = [];
    while (start.isSameOrBefore(end, 'month')) {
      months.push({
        value: start.format('MMM').toLowerCase(),
        label: start.format('MMMM'),
        start: start.startOf('month').unix(),
        end: start.endOf('month').unix(),
      });
      start.add(1, 'month');
    }
    return months;
  }, []);

  const getDynamicQuarters = useCallback((startTs, endTs) => {
    const start = moment.unix(startTs).startOf('quarter');
    const end = moment.unix(endTs);
    const quarters = [];
    let q = 1;
    while (start.isSameOrBefore(end, 'month')) {
      quarters.push({
        value: `q${q}`,
        label: `Q${q} (${start.format('MMM')}–${moment(start).endOf('quarter').format('MMM')})`,
        start: start.unix(),
        end: moment(start).endOf('quarter').unix(),
      });
      start.add(3, 'months').startOf('quarter');
      q++;
    }
    return quarters;
  }, []);

  const getDynamicHalfYears = useCallback((startTs, endTs) => {
    const start = moment.unix(startTs);
    const h1Start = start.clone().month(3).startOf('month');
    const h1End = h1Start.clone().add(5, 'months').endOf('month');
    const h2Start = h1End.clone().add(1, 'day').startOf('month');
    const h2End = h2Start.clone().add(5, 'months').endOf('month');

    return [
      {
        value: 'h1',
        label: `H1 (${h1Start.format('MMM')}–${h1End.format('MMM')})`,
        start: h1Start.unix(),
        end: h1End.unix(),
      },
      {
        value: 'h2',
        label: `H2 (${h2Start.format('MMM')}–${h2End.format('MMM')})`,
        start: h2Start.unix(),
        end: h2End.unix(),
      },
    ];
  }, []);

  useEffect(() => {
    if (!isPreferenceLoaded || isLoading || !listFYData?.length) return;

    const preferenceKey = `${defaultOffsetMonths}-${companyPreference?.length || 0}-${listFYData?.length || 0}`;
    if (lastProcessedPreference.current === preferenceKey && isInitialized.current) return;

    const typeCd = defaultOffsetMonths === 0 ? 'Current Period' : 'Compare Period';
    let hasPref = false;

    if (companyPreference?.length) {
      const pref = companyPreference.find((p) => p.attributetypecd === typeCd);

      if (pref) {
        const fyData = listFYData.find((f) => f.financialyearid === pref.attributevalue);

        if (fyData) {
          hasPref = true;

          setFy({
            label: `FY ${fyData.startyear}-${fyData.endyear}`,
            value: fyData.financialyearid,
            startTimestamp: fyData.startdate,
            endTimestamp: fyData.enddate,
            companyid: fyData.companyid,
          });

          const [value = '', type = 'monthly'] = (pref.additionalvalue || '').split('/');
          setSelectedType(type);

          if (type === 'custom') {
            const [start, end] = (value || '').split('-');
            if (start && end) {
              setCustomDates({ start, end });
              setSelectedVal('Custom Date');
            }
          } else {
            setSelectedVal(value);
          }
        }
      }
    }

    if (!hasPref && defaultOffsetMonths === 1 && financialYear && Object.keys(financialYear).length > 0) {
      const prevStartYear = Number(financialYear.startyear) - 1;

      const prevFY =
        listFYData.find(
          (f) => Number(f.startyear) === prevStartYear && (financialYear.companyid ? f.companyid === financialYear.companyid : true)
        ) || listFYData.find((f) => Number(f.startyear) === prevStartYear);

      if (prevFY) {
        setFy({
          label: `FY ${prevFY.startyear}-${prevFY.endyear}`,
          value: prevFY.financialyearid,
          startTimestamp: prevFY.startdate,
          endTimestamp: prevFY.enddate,
          companyid: prevFY.companyid,
        });
        setSelectedType('fullyear');
        setSelectedVal('Full Year');
      }
    }

    // Apply default financial year only if no preference exists and it's current period
    if (!hasPref && defaultOffsetMonths === 0 && financialYear && Object.keys(financialYear).length > 0) {
      const { startdate, enddate, startyear, endyear, financialyearid, companyid } = financialYear;

      setFy({
        label: startyear && endyear ? `FY ${startyear}-${endyear}` : null,
        value: financialyearid,
        startTimestamp: startdate,
        endTimestamp: enddate,
        companyid,
      });
      setSelectedType('fullyear');
      setSelectedVal('Full Year');
    }

    // Update refs to prevent duplicate processing
    lastProcessedPreference.current = preferenceKey;
    isInitialized.current = true;
  }, [isPreferenceLoaded, isLoading, companyPreference, defaultOffsetMonths, financialYear, listFYData]);


  const months = useMemo(() => (fy ? getDynamicMonths(fy.startTimestamp, fy.endTimestamp) : []), [fy, getDynamicMonths]);
  const quarters = useMemo(() => (fy ? getDynamicQuarters(fy.startTimestamp, fy.endTimestamp) : []), [fy, getDynamicQuarters]);
  const halfYears = useMemo(() => (fy ? getDynamicHalfYears(fy.startTimestamp, fy.endTimestamp) : []), [fy, getDynamicHalfYears]);

  const periodTypes = useMemo(
    () => [
      { value: 'monthly', label: 'Monthly', children: months },
      { value: 'quarterly', label: 'Quarterly', children: quarters },
      { value: 'halfyearly', label: 'Half Yearly', children: halfYears },
      {
        value: 'fullyear',
        label: 'Full Year',
        children: [
          {
            value: 'fullyear',
            label: 'Full Year',
            start: fy?.startTimestamp,
            end: fy?.endTimestamp,
          },
        ],
      },
      { value: 'custom', label: 'Custom Date', isCustom: true },
    ],
    [months, quarters, halfYears, fy?.startTimestamp, fy?.endTimestamp]
  );

  const closeAll = useCallback((type = 'all') => {
    if (type === 'all') {
      setAnchorEl(null);
      setSubAnchorEl(null);
    } else if (type === 'sub') {
      setSubAnchorEl(null);
    }
  }, []);

  const openMainMenu = useCallback(
    (evt) => {
      if (!fy) return;
      setAnchorEl(evt.currentTarget);
    },
    [fy]
  );

  const handleType = useCallback(
    (type) => (evt) => {
      if (!fy) return;
      const cfg = periodTypes.find((p) => p.value === type);
      setSelectedVal('');
      setSelectedType(type);

      if (cfg?.value === 'fullyear') {
        handleSubSelect(cfg?.label, type);
        return;
      }
      if (cfg?.isCustom) {
        setCustomOpen(true);
        setSubAnchorEl(evt.currentTarget);
      } else if (cfg?.children) {
        setCustomOpen(false);
        setSubAnchorEl(evt.currentTarget);
      } else {
        closeAll();
      }
    },
    [periodTypes, closeAll]
  );

  const handleSubSelect = useCallback(
    async (label, type) => {
      if (!fy?.value) return;

      const attributetypecd = defaultOffsetMonths === 0 ? 'Current Period' : 'Compare Period';

      // Start common loading
      startCommonLoading();

      // Update UI immediately for better UX
      setSelectedVal(label);
      closeAll();

      // Save preference in background
      try {
        await saveCompanyPreference({
          attributetypecd,
          additionalvalue: label,
          attributevalue: fy.value,
          selectedType: type,
        });

        // Simulate data refetch delay (replace with actual data refetch logic)
        setTimeout(() => {
          stopCommonLoading();
        }, 2000);
      } catch (error) {
        // Stop loading on error
        stopCommonLoading();
        console.error('Failed to save preference:', error);
      }
    },
    [defaultOffsetMonths, fy?.value, saveCompanyPreference, closeAll, startCommonLoading, stopCommonLoading]
  );

  const onFyChange = useCallback(
    (_, newFy) => {
      // Start common loading
      startCommonLoading();

      // Update UI state immediately
      setFy(newFy);
      setSelectedType('');
      setSelectedVal('');
      setCustomDates({ start: null, end: null });
      setCustomOpen(false);

      // Save preference in background if needed
      if (!newFy) {
        const attributetypecd = defaultOffsetMonths === 0 ? 'Current Period' : 'Compare Period';
        saveCompanyPreference({
          attributetypecd,
          additionalvalue: '',
          attributevalue: 0,
          selectedType: '',
        })
          .then(() => {
            // Simulate data refetch delay
            setTimeout(() => {
              stopCommonLoading();
            }, 2000);
          })
          .catch((error) => {
            stopCommonLoading();
            console.error('Failed to clear preference:', error);
          });
      } else {
        // Simulate data refetch delay
        setTimeout(() => {
          stopCommonLoading();
        }, 2000);
      }
    },
    [defaultOffsetMonths, saveCompanyPreference, startCommonLoading, stopCommonLoading]
  );

  const handleCustomDateChange = useCallback(
    async ({ from, to }) => {
      setCustomDates((prev) => {
        let start = from !== undefined ? from : prev.start;
        let end = to !== undefined ? to : prev.end;

        const next = { start, end };

        if (next.start && next.end) {
          // Start common loading
          startCommonLoading();

          // Update UI immediately
          setSelectedVal('Custom Date');
          closeAll();
          setCustomOpen(false);

          // Save preference in background
          const attributetypecd = defaultOffsetMonths === 0 ? 'Current Period' : 'Compare Period';
          const label = `${next.start}-${next.end}`;

          saveCompanyPreference({
            attributetypecd,
            additionalvalue: label,
            attributevalue: fy?.value,
            selectedType: 'custom',
          })
            .then(() => {
              // Simulate data refetch delay
              setTimeout(() => {
                stopCommonLoading();
              }, 2000);
            })
            .catch((error) => {
              stopCommonLoading();
              console.error('Failed to save custom date preference:', error);
            });
        }

        return next;
      });
    },
    [defaultOffsetMonths, fy?.value, saveCompanyPreference, closeAll, startCommonLoading, stopCommonLoading]
  );

  return {
    // State
    fy,
    selectedType,
    selectedVal,
    customDates,
    customOpen,

    // Computed values
    financialYears,
    periodTypes,

    // UI state
    anchorEl,
    subAnchorEl,

    // Actions
    openMainMenu,
    closeAll,
    handleType,
    handleSubSelect,
    handleCustomDateChange,
    onFyChange,
    setCustomOpen,

    // Loading states
    isLoading: isLoading || isFetching,
    isCreating: creating,

    // Common loading states
    isCommonLoading,
    startCommonLoading,
    stopCommonLoading,

    // Error states
    hasError: !!fetchError || !!createError,
    error: fetchError || createError,
  };
};
